import React, { type ReactElement, type ReactNode, useEffect, useState } from 'react'

import { Modal } from 'components'
import { clearTabBlockers, setDirty } from 'store/StructuralContext/structuralSlice'
import { useDispatch, useSelector } from 'store/store'

import * as S from './styles'

export type TabsChildren = {
  initialTab?: number
  children: ReactElement<TabProps> | ReactElement<TabProps>[]
  showTotalizer?: boolean
}

export type TabProps = {
  label: string
  disabled?: boolean
  totalizer?: number
  children?: ReactNode | ReactNode[]
}

export const Tabs = ({ children, initialTab = 0, showTotalizer = false }: TabsChildren) => {
  const dispatch = useDispatch()

  const [activeTab, setActiveTab] = useState<number>(initialTab)
  const [selectedTab, setSelectedTab] = useState<number>(initialTab)
  const [blockedTabModal, setBlockedTabModal] = useState<boolean>(false)
  const [tabLabels, setTabLabels] = useState<string[]>([])
  const [tabTotalizer, setTabTotalizer] = useState<number[]>([])
  const [disabledTabs, setDisabledTabs] = useState<boolean[]>([])
  const [childrenContents, setChildrenContents] = useState<ReactNode[]>([])

  const { tabBlockers } = useSelector(({ structure }) => structure)

  useEffect(() => {
    const labels: string[] = []
    const contents: ReactNode[] = []
    const totalizer: number[] = []
    const disabledTabsMap: boolean[] = []

    React.Children.map(children, (child, index) => {
      labels[index] = child.props.label
      contents[index] = child.props.children
      totalizer[index] = child.props.totalizer ?? 0
      disabledTabsMap[index] = child.props.disabled || false
    })

    setTabLabels(labels)
    setDisabledTabs(disabledTabsMap)
    setChildrenContents(contents)
    setTabTotalizer(totalizer)
  }, [children])

  const handleTabChange = (index: number) => {
    setSelectedTab(index)

    if (tabBlockers.length > 0) {
      setBlockedTabModal(true)
    } else {
      setActiveTab(index)
    }
  }

  const handleUnblockTab = () => {
    setBlockedTabModal(false)
    dispatch(clearTabBlockers())
    dispatch(setDirty(false))
    setActiveTab(selectedTab)
  }

  return (
    <>
      <S.Wrapper>
        <S.TabsRow>
          {tabLabels.map((label, index) => (
            <S.TabItem
              active={index === activeTab}
              disabled={disabledTabs[index]}
              key={label}
              onClick={() => handleTabChange(index)}
            >
              <S.TabTitle>
                {label}{' '}
                {showTotalizer && <S.TabTotalizer>{`(${tabTotalizer[index]})`}</S.TabTotalizer>}
              </S.TabTitle>
            </S.TabItem>
          ))}
        </S.TabsRow>

        {Object.keys(childrenContents).map((key, index) => {
          if (index === activeTab) {
            return (
              <S.ContentContainer key={`contents-${key}`}>
                {childrenContents[activeTab]}
              </S.ContentContainer>
            )
          }
        })}
      </S.Wrapper>

      <Modal
        buttonAction={() => setBlockedTabModal(false)}
        buttonText="CONTINUAR A EDIÇÃO"
        description="Existem campos que não foram salvos, deseja prosseguir mesmo assim?"
        open={blockedTabModal}
        returnAction={handleUnblockTab}
        returnIcon={<></>}
        returnText="DESCARTAR O PROGRESSO"
        setOpen={(open: boolean) => setBlockedTabModal(open)}
        showReturn
        title="Continuar de onde parou"
      />
    </>
  )
}

const Tab = ({ label, children }: TabProps) => <div key={label}>{children}</div>
Tabs.Tab = Tab
